import React, { useRef } from 'react';
import {
  Sparkles,
  Calendar,
  Layers,
  Copy,
  Check,
  BookOpen,
  Plus,
  Info,
  Network,
  GitBranch,
  CheckCircle2,
} from 'lucide-react';
import type { Book, EditionTreeNode } from '../../../../shared/types/index.js';
import { getEditionTypeInfo, getStatusBadge } from './editionTreeUtils.js';

interface EditionDiagramViewProps {
  tree: EditionTreeNode;
  currentBookId: string;
  selectedNode: Book;
  onSelectNode: (node: Book) => void;
  copiedUibn: string | null;
  onCopyUibn: (uibn: string, e?: React.MouseEvent) => void;
  onOpenBookDetail?: (book: Book) => void;
  onRequestCreateEdition?: (parentBook: Book) => void;
  treeViewMode: 'diagram' | 'list';
  onTreeViewModeChange: (mode: 'diagram' | 'list') => void;
}

export const EditionDiagramView: React.FC<EditionDiagramViewProps> = ({
  tree,
  currentBookId,
  selectedNode,
  onSelectNode,
  copiedUibn,
  onCopyUibn,
  onOpenBookDetail,
  onRequestCreateEdition,
  treeViewMode,
  onTreeViewModeChange,
}) => {
  const treeScrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollTree = (direction: 'left' | 'right' | 'center') => {
    if (!treeScrollContainerRef.current) return;
    const container = treeScrollContainerRef.current;
    if (direction === 'left') {
      container.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (direction === 'right') {
      container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
    } else {
      container.scrollTo({ left: (container.scrollWidth - container.clientWidth) / 2, behavior: 'smooth' });
    }
  };

  // Render tree node recursively (Diagram Mode)
  const renderTreeNode = (node: EditionTreeNode, isRoot = false) => {
    const isCurrent = node.id === currentBookId;
    const isSelected = selectedNode.id === node.id;
    const typeInfo = getEditionTypeInfo(node.editionType);

    return (
      <div className="flex flex-col items-center shrink-0">
        {/* Card Component */}
        <div
          onClick={() => onSelectNode(node)}
          className={`relative w-[240px] sm:w-72 p-3 sm:p-4 rounded-xl border transition-all cursor-pointer shadow-xs hover:shadow-md ${
            isSelected
              ? 'bg-gradient-to-b from-indigo-50/90 to-white border-indigo-600 ring-2 ring-indigo-500/60 shadow-indigo-100'
              : isCurrent
              ? 'bg-gradient-to-b from-slate-50 to-white border-indigo-400 ring-1 ring-indigo-300 shadow-sm'
              : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
        >
          {/* Current Indicator Badge */}
          {isCurrent && (
            <div className="absolute -top-2.5 right-2 sm:right-3 bg-indigo-600 text-white text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-full shadow-xs flex items-center gap-1 z-10">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse"></span>
              Naskah Aktif
            </div>
          )}

          {/* Root Book Tag */}
          {isRoot && (
            <div className="absolute -top-2.5 left-2 sm:left-3 bg-slate-800 text-white text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-full shadow-xs flex items-center gap-1 z-10">
              <Sparkles className="w-2.5 h-2.5 text-amber-300" />
              Naskah Induk
            </div>
          )}

          <div className="flex items-start justify-between gap-1.5 mt-1 mb-1.5">
            <span
              className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-semibold border ${typeInfo.badge} truncate max-w-[130px] sm:max-w-none`}
            >
              {typeInfo.label}
            </span>
            {getStatusBadge(node.status)}
          </div>

          <h4 className="text-xs sm:text-sm font-bold text-slate-900 line-clamp-2 leading-snug mb-1">
            {node.title}
          </h4>

          <div className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-slate-500 mb-2 flex-wrap">
            <span className="font-semibold text-slate-700">
              {node.edition || 'Edisi 1'}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3 text-slate-400" />
              {node.publicationYear}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Layers className="w-3 h-3 text-slate-400" />
              {node.pages || 0} hlm
            </span>
          </div>

          {/* UIBN Display & Copy */}
          <div className="bg-slate-50 rounded-lg p-1.5 sm:p-2 flex items-center justify-between border border-slate-100 mb-2">
            <div className="font-mono text-[10px] sm:text-[11px] font-semibold text-slate-700 truncate">
              {node.uibn}
            </div>
            {node.status !== 'diajukan' && (
              <button
                type="button"
                onClick={(e) => onCopyUibn(node.uibn, e)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-200 transition shrink-0 ml-1 cursor-pointer"
                title="Salin UIBN"
              >
                {copiedUibn === node.uibn ? (
                  <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-600" />
                ) : (
                  <Copy className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                )}
              </button>
            )}
          </div>

          {/* Edition Notes / Changelog */}
          {node.editionNotes && (
            <div className="text-[10px] sm:text-[11px] text-slate-600 bg-amber-50/70 border border-amber-200/60 rounded-md p-1.5 sm:p-2 mb-2 line-clamp-2 italic">
              "{node.editionNotes}"
            </div>
          )}

          {/* Quick Node Actions */}
          <div className="flex items-center justify-between pt-1.5 sm:pt-2 border-t border-slate-100 text-xs">
            {onOpenBookDetail && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenBookDetail(node);
                }}
                className="text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1 text-[10px] sm:text-[11px] cursor-pointer"
              >
                <BookOpen className="w-3 h-3" />
                Buka Detail
              </button>
            )}

            {onRequestCreateEdition && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestCreateEdition(node);
                }}
                className="text-emerald-700 hover:text-emerald-800 font-semibold flex items-center gap-1 text-[10px] sm:text-[11px] ml-auto cursor-pointer"
              >
                <Plus className="w-3 h-3" />
                Tambah Turunan
              </button>
            )}
          </div>
        </div>

        {/* Children Branching */}
        {node.children && node.children.length > 0 && (
          <div className="flex flex-col items-center w-full">
            {/* Vertical connector from parent to branch line */}
            <div className="w-0.5 h-5 sm:h-6 bg-slate-300"></div>

            <div className="relative flex items-start gap-4 sm:gap-8 pt-3 sm:pt-4">
              {/* Horizontal line across children: centered at child card centers */}
              {node.children.length > 1 && (
                <div
                  className="absolute top-0 left-[120px] sm:left-36 right-[120px] sm:right-36 h-0.5 bg-slate-300"
                  style={{ transform: 'translateY(0)' }}
                ></div>
              )}

              {node.children.map((child, cIdx) => (
                <div key={`${child.id}-${cIdx}`} className="flex flex-col items-center shrink-0">
                  {/* Vertical connector from horizontal line to child */}
                  {node.children!.length > 1 && (
                    <div className="w-0.5 h-3 sm:h-4 bg-slate-300 -mt-3 sm:-mt-4 mb-0"></div>
                  )}
                  {renderTreeNode(child, false)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render hierarchical indented outline list (Mobile-friendly Outline View)
  const renderHierarchyList = (node: EditionTreeNode, depth = 0, index = 0) => {
    const isCurrent = node.id === currentBookId;
    const isSelected = selectedNode.id === node.id;
    const typeInfo = getEditionTypeInfo(node.editionType);

    return (
      <div key={`${node.id}-${depth}-${index}`} className="w-full">
        <div
          onClick={() => onSelectNode(node)}
          style={{
            marginLeft: depth > 0 ? `${Math.min(depth * 14, 42)}px` : '0px',
          }}
          className={`relative p-3 rounded-xl border transition cursor-pointer mb-2.5 shadow-2xs hover:shadow-xs ${
            isSelected
              ? 'bg-indigo-50/90 border-indigo-500 ring-2 ring-indigo-300/80'
              : isCurrent
              ? 'bg-slate-50 border-indigo-300'
              : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
        >
          {depth > 0 && (
            <div className="absolute -left-3 top-5 w-3 h-0.5 bg-indigo-300"></div>
          )}

          <div className="flex items-start justify-between gap-1.5 flex-wrap mb-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold border ${typeInfo.badge}`}>
                {typeInfo.label}
              </span>
              <span className="font-bold text-slate-900 text-xs sm:text-sm">
                {node.edition || 'Edisi 1'}
              </span>
              {getStatusBadge(node.status)}
              {isCurrent && (
                <span className="text-[10px] bg-indigo-600 text-white font-bold px-2 py-0.5 rounded-full">
                  Aktif
                </span>
              )}
              {depth === 0 && (
                <span className="text-[10px] bg-slate-800 text-amber-300 font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Sparkles className="w-2.5 h-2.5" /> Naskah Induk
                </span>
              )}
            </div>

            <span className="font-mono text-[10px] sm:text-[11px] text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
              {node.uibn}
            </span>
          </div>

          <h4 className="font-bold text-slate-900 text-xs sm:text-sm mb-1 leading-snug">
            {node.title}
          </h4>

          <div className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-slate-500 mb-1.5 flex-wrap">
            <span>Tahun {node.publicationYear}</span>
            <span>•</span>
            <span>{node.pages || 0} hlm</span>
            <span>•</span>
            <span>{node.language || 'Indonesia'}</span>
            <span>•</span>
            <span className="truncate max-w-[150px]">{node.publisherName}</span>
          </div>

          {node.editionNotes && (
            <div className="text-[11px] text-slate-700 bg-amber-50/80 border border-amber-200/70 rounded-lg p-2 my-1.5 italic">
              "{node.editionNotes}"
            </div>
          )}

          <div className="flex items-center justify-between pt-1.5 border-t border-slate-100 text-xs mt-1">
            <span className="text-indigo-600 font-medium text-[11px] flex items-center gap-1">
              {isSelected ? (
                <>
                  <CheckCircle2 className="w-3 h-3 text-indigo-600" />
                  <span>Sedang Aktif di Area Detail</span>
                </>
              ) : (
                'Ketuk untuk lihat detail lengkap'
              )}
            </span>

            <div className="flex items-center gap-1.5">
              {onOpenBookDetail && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenBookDetail(node);
                  }}
                  className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded text-[11px] transition cursor-pointer"
                >
                  Detail
                </button>
              )}
              {onRequestCreateEdition && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequestCreateEdition(node);
                  }}
                  className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 font-semibold rounded text-[11px] transition flex items-center gap-1 cursor-pointer"
                >
                  <Plus className="w-3 h-3" /> Edisi Baru
                </button>
              )}
            </div>
          </div>
        </div>

        {node.children && node.children.map((child, idx) => renderHierarchyList(child, depth + 1, idx))}
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col space-y-3">
      {/* Guidance & View Mode Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 bg-white p-2.5 sm:p-3 rounded-xl border border-slate-200 shadow-2xs">
        <div className="flex items-center gap-2 text-xs text-slate-700 min-w-0">
          <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg shrink-0">
            <Info className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <span className="font-semibold block text-slate-800 truncate">
              Bagan Hubungan Silsilah Naskah
            </span>
            <span className="text-[11px] text-slate-500">
              Ketuk kartu manapun untuk membuka spesifikasi & catatan revisi di area detail.
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 self-start sm:self-auto shrink-0 flex-wrap">
          {/* Scroll quick navigation (only for diagram mode) */}
          {treeViewMode === 'diagram' && (
            <div className="flex items-center gap-0.5 bg-slate-100 p-0.5 rounded-lg border border-slate-200/80 text-xs">
              <button
                type="button"
                onClick={() => scrollTree('left')}
                className="px-2 py-1 text-slate-600 hover:text-slate-900 hover:bg-white rounded transition text-[11px] font-medium cursor-pointer"
                title="Geser ke sisi kiri"
              >
                Kiri
              </button>
              <button
                type="button"
                onClick={() => scrollTree('center')}
                className="px-2 py-1 text-slate-600 hover:text-slate-900 hover:bg-white rounded transition text-[11px] font-medium cursor-pointer"
                title="Pusatkan posisi bagan"
              >
                Pusat
              </button>
              <button
                type="button"
                onClick={() => scrollTree('right')}
                className="px-2 py-1 text-slate-600 hover:text-slate-900 hover:bg-white rounded transition text-[11px] font-medium cursor-pointer"
                title="Geser ke sisi kanan"
              >
                Kanan
              </button>
            </div>
          )}

          {/* Switcher: Diagram vs Daftar Bertingkat */}
          <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200/80 text-xs">
            <button
              type="button"
              onClick={() => onTreeViewModeChange('diagram')}
              className={`px-2.5 py-1 rounded-md transition text-xs font-semibold flex items-center gap-1.5 cursor-pointer ${
                treeViewMode === 'diagram'
                  ? 'bg-white text-indigo-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Network className="w-3.5 h-3.5" />
              Bagan Diagram
            </button>
            <button
              type="button"
              onClick={() => onTreeViewModeChange('list')}
              className={`px-2.5 py-1 rounded-md transition text-xs font-semibold flex items-center gap-1.5 cursor-pointer ${
                treeViewMode === 'list'
                  ? 'bg-white text-indigo-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <GitBranch className="w-3.5 h-3.5" />
              Daftar Bertingkat
            </button>
          </div>
        </div>
      </div>

      {treeViewMode === 'diagram' ? (
        <div
          ref={treeScrollContainerRef}
          className="w-full overflow-x-auto overflow-y-visible py-4 rounded-xl border border-slate-200/80 bg-white/60 shadow-2xs"
        >
          <div className="w-max min-w-full flex flex-col items-center px-6 sm:px-12 py-2">
            {renderTreeNode(tree, true)}
          </div>
        </div>
      ) : (
        <div className="w-full bg-white rounded-xl border border-slate-200 p-3 sm:p-5 shadow-2xs">
          <div className="text-xs font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
            Hierarki Silsilah Naskah (Hubungan Induk & Edisi Turunan):
          </div>
          {renderHierarchyList(tree, 0)}
        </div>
      )}
    </div>
  );
};
